<script setup lang="ts">
  import { useTableProperties } from '@/features/FormBuilder/model/useTableProperties';
  import { Label, NumberStepperInput } from '@/shared/ui';
  import { computed } from 'vue';
  import { useDebounceFn } from '@vueuse/core';
  import { useFormBuilderToast } from '../../../composables/useFormBuilderToast';
  import { useTableEditorStore } from '../../../model/table-editor.store';
  import { TABLE_CONSTRAINTS } from '../../../lib/table-constants';
  import TableDesignSection from './TableDesignSection.vue';
  import TableSettingsSection from './TableSettingsSection.vue';

  interface Props {
    elementId: string;
  }

  const props = defineProps<Props>();

  const tableEditorStore = useTableEditorStore();
  const { showWarningToast } = useFormBuilderToast();

  const MAX_ROWS = TABLE_CONSTRAINTS.MAX_ROWS;
  const MAX_COLUMNS = TABLE_CONSTRAINTS.MAX_COLUMNS;

  const rowsInputId = computed(() => `table-rows-${props.elementId}`);
  const columnsInputId = computed(() => `table-columns-${props.elementId}`);

  const { element } = useTableProperties(props.elementId);

  const rows = computed(() => element.value?.properties.rows ?? 3);
  const columns = computed(() => element.value?.properties.columns ?? 3);

  const normalizeDimensionValue = (value: number, min: number, max?: number): number => {
    const normalizedValue = Math.max(min, Math.round(value));
    if (max === undefined) {
      return normalizedValue;
    }
    return Math.min(normalizedValue, max);
  };

  const warnOnLimit = (result: { success: boolean; message?: string } | undefined) => {
    if (result && !result.success && result.message) {
      showWarningToast({
        title: result.message,
        type: 'warning',
        hideIcon: true,
      });
    }
  };

  // Apply the whole delta in a single batched store mutation, debounced so a burst of
  // keystrokes (typing "1000" → 10, 100, 1000) or rapid stepper clicks collapses into one
  // rebuild instead of one mutation per intermediate value.
  const COMMIT_DEBOUNCE_MS = 300;

  const commitRows = useDebounceFn((target: number) => {
    warnOnLimit(tableEditorStore.setRowCount(target));
  }, COMMIT_DEBOUNCE_MS);

  const commitColumns = useDebounceFn((target: number) => {
    warnOnLimit(tableEditorStore.setColumnCount(target));
  }, COMMIT_DEBOUNCE_MS);

  const handleRowsChange = (nextRows: number | null) => {
    if (nextRows === null) {
      return;
    }

    commitRows(normalizeDimensionValue(nextRows, 1, MAX_ROWS));
  };

  const handleColumnsChange = (nextColumns: number | null) => {
    if (nextColumns === null) {
      return;
    }

    commitColumns(normalizeDimensionValue(nextColumns, 1, MAX_COLUMNS));
  };
</script>

<template>
  <div
    v-if="element"
    class="space-y-4 pt-4 font-base text-[var(--color-gray-07)]"
  >
    <TableSettingsSection :element-id="props.elementId" />

    <div class="mb-2 border-b border-gray-02 pb-4">
      <h3 class="font-medium mb-2">
        Estrutura da Tabela
      </h3>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <Label
            :for="rowsInputId"
            class="text-sm font-normal mb-2"
          >Linhas</Label>
          <NumberStepperInput
            :input-id="rowsInputId"
            :model-value="rows"
            :min="1"
            :max="MAX_ROWS"
            :step="1"
            input-class="w-full pr-9"
            wrapper-class="w-full"
            aria-label="Quantidade de linhas da tabela"
            @update:model-value="handleRowsChange"
          />
        </div>

        <div class="flex flex-col">
          <Label
            :for="columnsInputId"
            class="text-sm font-normal mb-2"
          >Colunas</Label>
          <NumberStepperInput
            :input-id="columnsInputId"
            :model-value="columns"
            :min="1"
            :max="MAX_COLUMNS"
            :step="1"
            input-class="w-full pr-9"
            wrapper-class="w-full"
            aria-label="Quantidade de colunas da tabela"
            @update:model-value="handleColumnsChange"
          />
        </div>
      </div>
    </div>

    <TableDesignSection :element-id="props.elementId" />
  </div>
</template>
